import { useEffect, useState } from 'react';

import { Button, Typography } from '@capsule/consumer-design-system';

import { useHistory } from 'react-router';
import { css } from 'styled-components';

import CapsuleIcon from 'src/app/components/Elements/CapsuleIcon';
import type { HealthProfileMedicationTransferrable } from 'src/app/hooks/data/healthProfile';
import { emptyHealthProfileData } from 'src/app/hooks/feature_data/useHealthProfilePersistentData';
import { useSegmentEvent } from 'src/app/hooks/useSegmentEvent';
import { medications } from 'src/app/router/paths';
import { toTitleCase } from 'src/app/utils/stringUtils';
import { CAPSULE_ICON_STRINGS } from 'src/consts';

import type { HealthProfileFormProps } from '..';
import { SimpleFeatureLayout } from '../../../FeatureLayouts/SimpleFeatureLayout/SimpleFeatureLayout';
import { HealthProfileUpdateStepNames } from '../constants';
import { getMedicationsTransferEligibility } from '../utils';

import { DoctorItem, DoctorLabel } from './styles';

export const JustSayCapsuleConfirmation = (props: HealthProfileFormProps) => {
  const { onToggleHideHeader, setHealthProfilePersistentData, healthProfilePersistentData, isLoadingPersistentData } =
    props;

  const currentStepName = HealthProfileUpdateStepNames.JustSayCapsule;
  const history = useHistory();
  const goHome = () => history.push(medications.route);

  useSegmentEvent('viewHealthProfileFormConfirmation', {
    shouldTrackOnMount: true,
    payload: { type: 'tell_your_doctor' },
  });
  useEffect(() => onToggleHideHeader(false));
  const [hasResetData, setHasResetData] = useState(false);
  const [providers, setProviders] = useState<string[]>([]);

  useEffect(() => {
    if (!hasResetData && !isLoadingPersistentData) {
      const prescriberSet = new Set<string>();
      const { doctorRequiredMeds, eligibleMeds } = getMedicationsTransferEligibility(
        healthProfilePersistentData.selectedMeds
      );

      [...doctorRequiredMeds, ...eligibleMeds].forEach((med: HealthProfileMedicationTransferrable) => {
        if ('prescriber' in med && med.prescriber.firstName && med.prescriber.lastName) {
          prescriberSet.add(`${toTitleCase(med.prescriber.firstName)} ${toTitleCase(med.prescriber.lastName)}`);
        }
      });

      setProviders(Array.from(prescriberSet));
      setHealthProfilePersistentData({ ...emptyHealthProfileData, lastVisit: new Date() });
      setHasResetData(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasResetData, isLoadingPersistentData, healthProfilePersistentData.selectedMeds]);

  return (
    <SimpleFeatureLayout
      backButton={{ isRendered: false }}
      keyName={currentStepName}
      title={
        <Typography
          css={css`
            margin-top: ${({ theme }) => theme.space.s4};
          `}
          variant={{ base: 'displayXl', smallDown: 'heading1' }}
        >
          Next time you're at the doctor, just say: “Capsule”
        </Typography>
      }
      description={
        <div
          css={css`
            margin-top: ${({ theme }) => theme.space.s2};
          `}
        >
          <Typography variant={'body'}>All doctors can send prescriptions to Capsule.</Typography>
        </div>
      }
      body={{
        content: (
          <div>
            {providers.map((provider: string) => {
              return (
                <DoctorItem>
                  <CapsuleIcon width={24} height={24} block iconString={CAPSULE_ICON_STRINGS.stethoscope_icon} />

                  <DoctorLabel>{provider}</DoctorLabel>
                </DoctorItem>
              );
            })}
            <Button
              variant="contained-secondary-lg"
              isFullWidth
              onClick={goHome}
              css={css`
                margin-top: ${({ theme }) => theme.space.s6};
              `}
            >
              Got It
            </Button>
          </div>
        ),
      }}
    />
  );
};
